import { useCallback, useEffect, useRef } from 'react';

import {
  type InputEvent,
  type Session,
  sendInputEvents,
  sendStatistics,
} from '../api/session';
import {
  KEY_MAP,
  MODIFIER_KEYS,
  mapToVideoCoordinates,
} from '../utils/inputMappings';
import { KeyboardLayoutManager } from '../utils/keyboardLayout';
import {
  isSessionAccessDenied,
  redirectSessionTerminal,
} from '../utils/sessionRedirect';

interface UseInputForwardingProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  session: Session | null;
  isConnected: boolean;
}

const isTextInput = (el: Element | null): el is HTMLElement =>
  el instanceof HTMLElement &&
  (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

const blurActiveTextInput = () => {
  if (isTextInput(document.activeElement)) {
    document.activeElement.blur();
  }
};

export const useInputForwarding = ({
  canvasRef,
  session,
  isConnected,
}: UseInputForwardingProps) => {
  const eventBuffer = useRef<InputEvent[]>([]);
  const isSending = useRef(false);
  const grabKeyEvents = useRef(false);
  const pressedKeys = useRef<Record<number, boolean>>({});
  const lastScrollInfo = useRef<{
    direction: number;
    time: number;
    skipped: number;
  } | null>(null);

  const layoutManagerRef = useRef<KeyboardLayoutManager | null>(null);
  if (!layoutManagerRef.current) {
    layoutManagerRef.current = new KeyboardLayoutManager();
  }
  const activeLayoutManager = layoutManagerRef.current;

  useEffect(() => {
    void layoutManagerRef.current?.initFromBrowser();
  }, []);

  const activeWindowId = session?.windows?.[0];

  const trySendEvents = useCallback(async () => {
    if (
      eventBuffer.current.length === 0 ||
      isSending.current ||
      !session?.id ||
      !activeWindowId
    ) {
      return;
    }

    isSending.current = true;
    const eventsToSend = [...eventBuffer.current];
    eventBuffer.current = [];

    try {
      await sendInputEvents(session.id, activeWindowId, eventsToSend);
    } catch (error) {
      if (isSessionAccessDenied(error)) {
        void sendStatistics(session.id, {});
        redirectSessionTerminal(
          session.properties?.seastarLocation,
          'SessionEnd',
        );
        return;
      }
      console.error('Failed to send input events:', error);
    } finally {
      isSending.current = false;
      // If more events came in while sending, try sending again
      if (eventBuffer.current.length > 0) {
        trySendEvents();
      }
    }
  }, [session?.id, session?.properties?.seastarLocation, activeWindowId]);

  const appendEvent = useCallback(
    (event: InputEvent, doNotSend = false) => {
      eventBuffer.current.push(event);
      if (!doNotSend) {
        trySendEvents();
      }
    },
    [trySendEvents],
  );

  const sessionId = session?.id;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isConnected || !sessionId) return;

    const handleMouseOver = () => {
      grabKeyEvents.current = true;
    };

    const handleMouseLeave = () => {
      grabKeyEvents.current = false;
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const handleBlur = () => {
      // Release all pressed keys
      const keys = Object.keys(pressedKeys.current).map(Number);
      let shouldSend = false;
      keys.forEach((keyCode) => {
        if (pressedKeys.current[keyCode]) {
          appendEvent(
            {
              type: 'keyUp',
              keyCode,
              keyboardLayout: activeLayoutManager.currentLayoutName,
            },
            true,
          );
          pressedKeys.current[keyCode] = false;
          shouldSend = true;
        }
      });
      if (shouldSend) {
        trySendEvents();
      }
    };

    const handleInputEvent = (e: Event) => {
      // Type guard for different event types
      const mouseEvent = e as MouseEvent;
      const keyboardEvent = e as KeyboardEvent;
      const wheelEvent = e as WheelEvent;

      const eventType = e.type;

      // Handle keyboard events
      if (eventType.startsWith('key')) {
        const code = keyboardEvent.code;
        const keyCode = KEY_MAP[code];
        if (!code || keyCode === undefined) return;

        // Ignore keyup for keys we are not tracking as pressed (e.g. after
        // synthesizing keyUp for Cmd+C on macOS, when a real keyup arrives later).
        if (eventType === 'keyup' && !pressedKeys.current[keyCode]) {
          return;
        }

        // Always forward keyup for keys we previously captured, even if focus
        // moved to an input/textarea (otherwise the remote key stays stuck).
        const isReleasingCapturedKey =
          eventType === 'keyup' && Boolean(pressedKeys.current[keyCode]);

        // Don't capture keyboard events when the user is typing in an input/textarea
        if (
          isTextInput(e.target as Element | null) &&
          !isReleasingCapturedKey
        ) {
          return;
        }

        if (!grabKeyEvents.current && !isReleasingCapturedKey) {
          return;
        }
        e.preventDefault();

        if (eventType === 'keydown') {
          const newLayout =
            activeLayoutManager.detectFromKeystroke(keyboardEvent);
          if (newLayout) {
            activeLayoutManager.setCurrentLayout(newLayout);
          }
        }

        const type = eventType === 'keyup' ? 'keyUp' : 'keyDown';
        pressedKeys.current[keyCode] = eventType === 'keydown';

        const inputEvent: InputEvent = {
          type,
          keyCode,
          keyboardLayout: activeLayoutManager.currentLayoutName,
        };

        // macOS does not fire keyup for non-modifier keys while Meta (Cmd) is
        // held. Synthesize an immediate matching keyUp so the guest does not
        // auto-repeat (e.g. Cmd+C / Cmd+V).
        const createMatchingKeyUpEvent =
          eventType === 'keydown' &&
          keyboardEvent.metaKey &&
          !MODIFIER_KEYS.includes(code);

        appendEvent(inputEvent, createMatchingKeyUpEvent);
        if (createMatchingKeyUpEvent) {
          pressedKeys.current[keyCode] = false;
          appendEvent({
            ...inputEvent,
            type: 'keyUp',
          });
        }
        return;
      }

      // Handle mouse events
      if (eventType.startsWith('mouse')) {
        grabKeyEvents.current = true;
        if (eventType === 'mousedown') {
          blurActiveTextInput();
        }
        // Map mouse coordinates to video space, accounting for object-fit: contain
        // which may letterbox/pillarbox the video within the canvas element
        const videoCoords = mapToVideoCoordinates(
          mouseEvent.offsetX,
          mouseEvent.offsetY,
          canvas.offsetWidth,
          canvas.offsetHeight,
          canvas.width,
          canvas.height,
        );

        // Ignore events outside the video bounds (in letterbox/pillarbox area)
        if (!videoCoords) {
          return;
        }

        const inputEvent: InputEvent = {
          type: '',
          mouseX: videoCoords.x,
          mouseY: videoCoords.y,
        };

        if (eventType === 'mousemove') {
          inputEvent.type = 'mouseMove';
        } else if (eventType === 'mousedown' || eventType === 'mouseup') {
          let button: 'left' | 'middle' | 'right' | undefined;
          switch (mouseEvent.button) {
            case 0:
              button = 'left';
              break;
            case 1:
              button = 'middle';
              break;
            case 2:
              button = 'right';
              break;
          }
          if (button) {
            inputEvent.mouseButton = button;
            inputEvent.type =
              eventType === 'mousedown' ? 'mouseDown' : 'mouseUp';
          }
        }

        if (inputEvent.type) {
          appendEvent(inputEvent);
        }
        return;
      }

      // Handle wheel events
      if (eventType === 'wheel') {
        e.preventDefault();
        if (wheelEvent.deltaY === 0) return;

        const direction = wheelEvent.deltaY > 0 ? -3 : 3;
        const now = Date.now();

        // Scroll throttling logic from reference
        if (lastScrollInfo.current === null) {
          lastScrollInfo.current = {
            direction,
            time: now,
            skipped: 0,
          };
        } else {
          if (lastScrollInfo.current.direction === direction) {
            const diff = now - lastScrollInfo.current.time;
            if (lastScrollInfo.current.skipped < 3) {
              if (diff < 50) {
                lastScrollInfo.current.skipped++;
                return;
              }
            } else {
              if (diff < 100) {
                lastScrollInfo.current.skipped++;
                return;
              }
            }
          }
        }
        lastScrollInfo.current.direction = direction;
        lastScrollInfo.current.time = now;
        lastScrollInfo.current.skipped = 0;

        // Map scroll coordinates to video space, accounting for object-fit: contain
        const videoCoords = mapToVideoCoordinates(
          wheelEvent.offsetX,
          wheelEvent.offsetY,
          canvas.offsetWidth,
          canvas.offsetHeight,
          canvas.width,
          canvas.height,
        );

        // Ignore scroll events outside the video bounds
        if (!videoCoords) {
          return;
        }

        const inputEvent: InputEvent = {
          type: 'mouseScroll',
          mouseX: videoCoords.x,
          mouseY: videoCoords.y,
          deltaY: direction,
        };
        appendEvent(inputEvent);
      }
    };

    // Attach listeners
    canvas.addEventListener('mouseover', handleMouseOver);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('contextmenu', handleContextMenu);

    // Mouse events on canvas
    canvas.addEventListener('mousemove', handleInputEvent);
    canvas.addEventListener('mousedown', handleInputEvent);
    canvas.addEventListener('mouseup', handleInputEvent);
    canvas.addEventListener('wheel', handleInputEvent, { passive: false });

    // Keyboard events on document (to capture global keys when focused)
    document.addEventListener('keydown', handleInputEvent);
    document.addEventListener('keyup', handleInputEvent);
    window.addEventListener('blur', handleBlur);

    return () => {
      canvas.removeEventListener('mouseover', handleMouseOver);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('contextmenu', handleContextMenu);

      canvas.removeEventListener('mousemove', handleInputEvent);
      canvas.removeEventListener('mousedown', handleInputEvent);
      canvas.removeEventListener('mouseup', handleInputEvent);
      canvas.removeEventListener('wheel', handleInputEvent);

      document.removeEventListener('keydown', handleInputEvent);
      document.removeEventListener('keyup', handleInputEvent);
      window.removeEventListener('blur', handleBlur);
    };
  }, [
    canvasRef,
    isConnected,
    sessionId,
    appendEvent,
    trySendEvents,
    activeLayoutManager,
  ]);
};
